import { Help, RecentlyViewed } from "@carbon/icons-react";
import { useEffect } from "react";
import { type ErrorBoundaryPropsWithComponent } from "react-error-boundary";

import { Icon } from "@moment/design-system/Icon";

import { useLogger } from "~/components/canvas/hooks/useLogger";
import { EmptyState } from "~/components/errors/EmptyState";
import { CANVAS_NAME } from "~/strings";
import { Button } from "~/ui/Button";
import { toTitleCase } from "~/utils/format/string";

type CanvasFailureComponent = ErrorBoundaryPropsWithComponent["FallbackComponent"];

/**
 * <CanvasFailure/>
 * This component logs an error to datadog and tells user that canvas view
 * broke without crashing the entire app.
 *
 * TODO: All not-found use-cases need to use consistent handling.
 *       The other components I know of are:
 *        * `app/src/auth/AuthRequired.tsx`
 *        * `app/src/pages/404.page.tsx`
 *        * (there may be others)
 *
 *       Consider these scenarios:
 *        * Document does not exist:
 *            All users should receive 404 handling
 *        * Document exists but the user (or anonymous) does not have permissions:
 *            All users should receive 404 handling
 *        * Document exists and has permission but fails to load from network:
 *            All users should receive a network failure error
 *        * Document exists and has permission but fails to render:
 *            All users should receive a generic error page.
 *
 *       The TL;DR preventing us from solving this is that we do not know what
 *       error is triggering this boundary today - all uses case (not found,
 *       not accessible, failed to load, failed to render) are triggering the
 *       same behavior, except for where they aren't!
 *
 *       We can address this later since we are still improving
 *       the underlying service calls, which need to differentiate
 *       between not-found and not-accessible. When we do look at this,
 *       we should try to implement these cases, considering:
 *        * Do we show logged in users 401 vs 404?
 *        * Do we show a 404 page vs a redirect?
 *        * Do some special routes (settings?) deserve different handling?
 *        * Do we redirect to index or login for logged out users?
 *
 * Be really careful about adding logging or other side effects here.
 * They expose information about whether the path should or should not exist.
 * Even logs directly to datadog should be used with care and consistency,
 * because they can be seen in network inspection.
 */
export const CanvasFailure: CanvasFailureComponent = ({ error, resetErrorBoundary }) => {
	const logger = useLogger("CanvasFailure");

	useEffect(() => {
		logger.error("Canvas View Failure", {
			errorObject: error,
		});
	}, [error]);

	const icon = <Icon icon={Help} size={32} />;

	const ReloadCanvasButton = (
		<Button size="md" onClick={() => resetErrorBoundary()} startIcon={<RecentlyViewed />}>
			Reload {toTitleCase(CANVAS_NAME.SINGULAR)}
		</Button>
	);

	return (
		<EmptyState headline={"Something went wrong"} icon={icon}>
			{ReloadCanvasButton}
		</EmptyState>
	);
};
